import _ from 'lodash';
import React, { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Form, FormProps, InputNumber, Modal, notification } from 'antd';
import { useParams } from 'react-router-dom';

import useModalStore from 'stores/useModal';

import { getMessageCode } from 'constants/validate';
import { ReactComponent as IconClose } from 'assets/images/close.svg';
import { ParamsGenerateRooming } from 'services/api/type/group.type';
import { generateRooming } from 'services/api/module/group.api';
import { queryClient } from 'index';
import QUERY_KEYS from 'services/api/queryKeys';
import { useOpenBookingDetail } from 'hooks/useOpenBookingDetail';
import { useTranslation } from 'react-i18next';

interface Props {
  isShow: boolean;
  onCancel: () => void;
}

function GenerateGroupBookingModal({ isShow, onCancel }: Props) {
  const { t } = useTranslation();
  const MESSAGE_CODE = getMessageCode(t);
  const [form] = Form.useForm();
  const { groupId } = useParams();

  const { openBookingDetail } = useOpenBookingDetail();
  const { subGroupDetail } = useModalStore();

  const { mutateAsync: mutateGenerateRooming, isPending } = useMutation({
    mutationFn: (params: ParamsGenerateRooming) => {
      return generateRooming(params);
    }
  });

  useEffect(() => {
    if (!isShow) {
      form.resetFields();
    }
  }, [form, isShow]);

  const handleChangePassword = () => {
    form.submit();
  };

  const onFinish: FormProps['onFinish'] = async (values: any) => {
    if (!_.isEmpty(subGroupDetail)) {
      try {
        const result = await mutateGenerateRooming({
          sub_group_id: subGroupDetail.id,
          quantity: values.quantity,
          pax: values.pax
        });

        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_GROUP_BOOKING_DETAIL, Number(groupId)]
        });

        openBookingDetail(result.booking_line_id);
        onCancel();
        form.resetFields();
        // setIsOpenGroupRoomingList(true);
      } catch (err: any) {
        notification.error({
          message: err.error || t('common.error')
        });
      }
    }
  };

  return (
    <Modal
      title={t('groupPage.groupBooking.groupBookingModal.title')}
      centered
      width={480}
      open={isShow}
      onCancel={onCancel}
      closeIcon={<IconClose />}
      okText={t('common.actions.confirm')}
      cancelText={t('common.actions.cancelAction')}
      onOk={handleChangePassword}
      okButtonProps={{ className: 'ant-btn-secondary' }}
      className="modal-generate-group-booking"
      destroyOnClose
      confirmLoading={isPending}
    >
      <Form
        form={form}
        name="generate-group-booking-form"
        layout="horizontal"
        style={{ width: '100%' }}
        initialValues={{
          quantity: '',
          pax: ''
        }}
        wrapperCol={{ span: 16 }}
        labelCol={{ span: 8 }}
        onFinish={onFinish}
        autoComplete="off"
      >
        <Form.Item
          label={t('groupPage.groupBooking.groupBookingModal.numRooms')}
          name="quantity"
          rules={[
            {
              required: true,
              message: MESSAGE_CODE.REQUIRED_NUMBER_ROOM
            }
          ]}
        >
          <InputNumber className="w-full" />
        </Form.Item>

        <Form.Item
          label={t('groupPage.groupBooking.groupBookingModal.numPax')}
          name="pax"
          rules={[
            {
              required: true,
              message: MESSAGE_CODE.REQUIRED_PAX
            }
          ]}
        >
          <InputNumber className="w-full" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default GenerateGroupBookingModal;
